import { Check, WarningAmber } from "@mui/icons-material";
import { Box, Button, Card, CardContent, Grid, TextareaAutosize, TextField, Typography } from "@mui/material";
import React, { useContext, useEffect, useState } from "react";
import { PermissionContext } from "../../contexts/PermissionContext";
import { IStorageRequestActionsFormSection } from "../../interfaces";
import { Role } from "../../utils";
import { Validate } from "../../utils/Validate";
import { DeleteDraftDialog } from "../Shared/DeleteDraftDialog";
import { FormSectionHeader } from "../Shared/FormSectionHeader";
import { UnknownStatus } from "../Shared/UnknownStatus";
import { SendDiscrepanciesDialog } from "./SendDiscrepanciesDialog";

const DraftActions = (deleteCallback: () => void, submitCallback?: () => void) => {

    const [loading, setLoading] = useState<boolean>(false);
    const [showDeletePrompt, setShowDeletePrompt] = useState<boolean>(false);

    const onDelete = (confirm: boolean) => {

        if (confirm) {
            setShowDeletePrompt(false);
            setLoading(true);
            deleteCallback();

        } else setShowDeletePrompt(false);
    }

    return (
        <Card>

            <FormSectionHeader label="Submit Request" />

            <CardContent>


                <Typography variant="subtitle1">
                    Once you have provided a reason for storage and added all the materials you would like to include in the request, you can submit it for approval.
                </Typography>
                <Typography variant="subtitle1">
                    Before submitting, you have the option to delete this draft; you can not delete a request once it has been submitted.
                </Typography>
                <Button variant="contained" onClick={submitCallback} disabled={submitCallback === undefined}>
                    Submit
                </Button>
                <Button
                    variant="outlined"
                    color="error"
                    onClick={() => setShowDeletePrompt(true)}
                    disabled={loading}>Delete</Button>



            </CardContent>

            <DeleteDraftDialog open={showDeletePrompt} handleClose={onDelete} />

        </Card>
    );
}

const ApprovalActions = (
    allowed: boolean,
    approveCallback: () => void,
    rejectCallback: (r: string) => void,
) => {

    const [reasonPrompt, setReasonPrompt] = useState(false);
    const [reason, setReason] = useState("");

    return (
        <Card>

            <FormSectionHeader label="Request Approval" />

            {allowed ?
                <CardContent>


                    <Typography variant="subtitle1">
                        Please assess the materials in this storage request and either <strong>Approve</strong> or <strong>Reject</strong> the request.
                    </Typography>

                    <Button variant="contained" onClick={approveCallback} disabled={reasonPrompt}>
                        Approve
                    </Button>

                    <Button variant="outlined" onClick={() => setReasonPrompt(true)} disabled={reasonPrompt}>
                        Reject
                    </Button>

                    {reasonPrompt &&

                        <Box sx={{ my: 2 }}>

                            <Typography variant="subtitle1">
                                To <strong>Reject</strong> this request please provide a reason
                            </Typography>
                            <Grid container sx={{ my: 3 }}>
                                <Grid md={6} xs={12}>
                                    <TextField
                                        fullWidth
                                        multiline
                                        required
                                        label="Reason"
                                        value={reason}
                                        onChange={(e) => setReason(e.target.value)}
                                    />
                                </Grid>
                            </Grid>

                            <div>
                                <Button
                                    variant="contained"
                                    color="warning" onClick={() => rejectCallback(reason)}
                                    disabled={reason.length === 0}
                                >
                                    Confirm Rejection
                                </Button>
                                <Button variant="outlined" onClick={() => setReasonPrompt(false)}>
                                    Cancel
                                </Button>
                            </div>
                        </Box>

                    }

                </CardContent>

                :

                <CardContent>

                    <Typography variant="subtitle1" color="GrayText">
                        This storage request is awaiting approval from Asset Protection. There are currently no further actions available.
                    </Typography>

                </CardContent>
            }

        </Card>
    );
}

const ApprovedActions = (
    allowed: boolean,
    materialDiscrepancies: number,
    incompleteMaterials: boolean,
    alertCallback: () => void,
    completeCallback: (cancellationReason?: string) => void) => {


    useEffect(() => {
        setLoading(false);
    }, [materialDiscrepancies])

    const [loading, setLoading] = useState<boolean>(false);
    const [showSendPrompt, setShowSendPrompt] = useState<boolean>(false);
    const [cancellationReason, setCancellationReason] = useState<string>("");

    const onSend = (confirm: boolean) => {

        if (confirm) {
            setShowSendPrompt(false);
            setLoading(true);
            alertCallback();

        } else setShowSendPrompt(false);
    }

    const onComplete = (cancellationReason?: string) => {
        setLoading(true);
        completeCallback(cancellationReason)
    }

    return (
        <Card>

            <FormSectionHeader label="Receiving Material" />
            {allowed ?
                <CardContent>

                    {materialDiscrepancies === 0 &&
                        <Grid container>
                            <Grid xs={10} sx={{ pr: 3 }} item >
                                <Typography variant="h6">Material Discrepancies</Typography>
                                < Typography variant="subtitle1">
                                    Select the <WarningAmber color="disabled" sx={{ mb: 1 }} /> icon to add a discrepancy.
                                    Once all material discrepancies are noted you can send an alert to the requestor.
                                </Typography>
                            </Grid>
                            <Grid xs={2} container alignItems="center" justifyContent="right" item>
                                <Button variant="contained" disabled>Send Now</Button>
                            </Grid>
                        </Grid>
                    }

                    {materialDiscrepancies > 0 &&
                        <Grid container>
                            <Grid xs={10} sx={{ pr: 3 }} item >
                                <Typography variant="h6">Material Discrepancies</Typography>
                                < Typography variant="subtitle1">
                                    There are {materialDiscrepancies} materials with discrepancies noted. Once all material discrepancies are noted you can send an alert to the requestor.
                                </Typography>
                            </Grid>
                            <Grid xs={2} container alignItems="center" justifyContent="right" item>
                                <Button
                                    variant="contained"
                                    onClick={() => setShowSendPrompt(true)}
                                    disabled={loading}>Send Now</Button>
                            </Grid>
                        </Grid>
                    }

                    {materialDiscrepancies === -1 &&
                        <Grid container style={{ opacity: .5 }}>
                            <Grid xs={10} item>
                                <Typography variant="subtitle1" color="theme.success">
                                    <Check sx={{ mb: 1 }} /> Material discrepancies have been sent to the requestor.
                                </Typography>
                            </Grid>

                        </Grid>
                    }


                    <Grid container>
                        <Grid xs={10} item>
                            <Typography variant="h6">Cancel Storage Request</Typography>
                            <TextareaAutosize
                                placeholder="Enter a Cancellation Reason"
                                minRows={2}
                                style={{
                                    borderRadius: '5px',
                                    borderColor: '#ccc',
                                    minWidth: '620px',
                                    padding: '.5em'
                                }}
                                value={cancellationReason}
                                onChange={(e) => setCancellationReason(e.target.value)}
                            />
                        </Grid>
                        <Grid xs={2} container alignItems="center" justifyContent="right" item>
                            <Button
                                variant="contained"
                                color="warning"
                                onClick={() => onComplete(cancellationReason)}
                                disabled={loading || cancellationReason.length < 1}>Cancel</Button>
                        </Grid>
                    </Grid>

                    <Grid container style={{ opacity: (incompleteMaterials ? 0.5 : 1) }}>
                        <Grid xs={10} item>
                            <Typography variant="h6">Complete Storage Request</Typography>
                            <Typography variant="subtitle1">
                                Once all material has arrived and has been assigned a location, the storage request can be set to complete.
                                Once completed, material from this storage request will be available from the inventory.
                            </Typography>
                        </Grid>
                        <Grid xs={2} container alignItems="center" justifyContent="right" item>
                            <Button
                                variant="contained"
                                onClick={() => onComplete()}
                                disabled={loading || incompleteMaterials}>Complete</Button>
                        </Grid>
                    </Grid>

                </CardContent>
                :
                <CardContent>

                    <Typography variant="subtitle1" color="GrayText">
                        This storage request has been approved. There are currently no further actions available.
                    </Typography>

                </CardContent>
            }

            <SendDiscrepanciesDialog open={showSendPrompt} handleClose={onSend} />

        </Card>
    );
}

const RejectedActions = (
    closeCallback: () => void,
    submitCallback?: () => void
) => {
    return (
        <Card>

            <FormSectionHeader label="Close or Resubmit Request" />

            <CardContent>

                <Typography variant="subtitle1">
                    The storage request was rejected. You can close this storage request or amend it and resubmit for approval.
                </Typography>

                <Button variant="contained" onClick={submitCallback} disabled={submitCallback === undefined}>
                    Resubmit
                </Button>
                <Button variant="outlined" onClick={closeCallback}>
                    Close
                </Button>

            </CardContent>
        </Card>

    );
}

const Closed = (approved: boolean, cancellationReason?: string) => {
    return (
        <Card >

            {/*<FormSectionHeader label="Closed" />*/}
            <FormSectionHeader label={approved ? "Approved" : "Rejected"} />

            <CardContent>
                <Typography variant="subtitle1" style={{ margin: 0 }}>
                    The storage request was {cancellationReason ? "cancelled" : (approved ? "approved" : "rejected")} and closed. There are no further actions available.
                </Typography>

                {cancellationReason &&
                    <Typography variant="subtitle2" style={{ margin: 1 }}>
                        {cancellationReason}
                    </Typography>
                }

            </CardContent>
        </Card>
    );
}

export const StorageRequestActions: React.FC<IStorageRequestActionsFormSection> = ({
    status,
    formValid,
    materials,
    alertSent,
    onSubmit,
    onDelete,
    onApprove,
    onAlert,
    onReject,
    onClose,
    onComplete,
    readonly,
    ...props
}: IStorageRequestActionsFormSection) => {


    const [discrepancyCount, setDiscrepancyCount] = useState<number>(0);
    const [incompleteMaterials, setIncompleteMaterials] = useState(true);

    const permissionContext = useContext(PermissionContext);




    useEffect(() => {
        if (alertSent)
            setDiscrepancyCount(-1);
        else
            setDiscrepancyCount(materials ? materials.filter(x => x.discrepancy).length : 0);


        if (materials) {
            setIncompleteMaterials(false);

            materials.forEach(x => {
                if (!x.undelivered)
                    if (Validate.materialLocationDetails(x)) {
                        setIncompleteMaterials(true);
                    }
            });

        }

    }, [materials, alertSent])

    return (
        <div>
            {{
                0: UnknownStatus(),
                1: DraftActions(onDelete, formValid ? onSubmit : undefined),
                2: ApprovalActions(permissionContext.isRole(Role.Internal.AssetProtection), onApprove, onReject),
                3: ApprovedActions(permissionContext.isRole(Role.Internal.AssetProtection), discrepancyCount, incompleteMaterials, onAlert, onComplete),
                4: RejectedActions(onClose, formValid ? onSubmit : undefined),
                5: Closed(false),
                6: Closed(true)
            }[status]}
        </div>
    );

}